import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { SchemaPaymentsViewsService } from '@frontend/schema';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { PaymentsFeatureSettings } from '../../types/payment-hooks.types';
import {
  getLocationFeatureSettingsQueryKey,
  useLocationFeatureSettingsDirect,
} from '../locations/use-location-feature-settings';
import { getFeatureSettingNameFromPmsName } from './use-bulk-collections-feature';

export const useBulkCollectionsFeatureSettings = (
  pmsName: string,
  setIsButtonLoading: (val: boolean) => void,
  { onSuccess, onError }: { onSuccess?: () => void; onError?: () => void }
) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const queryClient = useQueryClient();
  const settingName = getFeatureSettingNameFromPmsName(pmsName ?? '');
  const alert = useAlert();

  const { refetchAll } = useLocationFeatureSettingsDirect(settingName as PaymentsFeatureSettings, {
    locationIds: selectedLocationIds,
  });

  const { mutate: toggleFeatureSetting } = useMutation(
    async (enabled: boolean) => {
      await Promise.all(
        selectedLocationIds.map((locationId) =>
          SchemaPaymentsViewsService.SetLocationFeatureEnabled({
            locationId,
            settingName,
            enabled,
          })
        )
      );
    },
    {
      onSuccess: () => {
        selectedLocationIds.forEach((locationId) => {
          queryClient.invalidateQueries(
            getLocationFeatureSettingsQueryKey(locationId, settingName as PaymentsFeatureSettings)
          );
        });
        refetchAll();
        onSuccess?.();
        alert.success(t('Collections enabled successfully'));
      },
      onError: () => {
        setIsButtonLoading(false);
        onError?.();
      },
    }
  );

  return {
    toggleFeatureSetting,
  };
};
